import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { EquipmentCard } from "@/components/EquipmentCard";
import { Input } from "@/components/ui/input";
import { Search, SlidersHorizontal, X } from "lucide-react";

const SORTS = [
  { value: "newest", label: "Newest First" },
  { value: "price_asc", label: "Price: Low → High" },
  { value: "price_desc", label: "Price: High → Low" },
  { value: "year_desc", label: "Year: New → Old" },
  { value: "hours_asc", label: "Hours: Low → High" },
];

export default function InventoryPage() {
  const [params, setParams] = useSearchParams();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [meta, setMeta] = useState({ categories: [], brands: [], conditions: [] });
  const [filtersOpen, setFiltersOpen] = useState(false);

  const filters = useMemo(() => ({
    category: params.get("category") || "all",
    brand: params.get("brand") || "all",
    condition: params.get("condition") || "all",
    search: params.get("search") || "",
    min_price: params.get("min_price") || "",
    max_price: params.get("max_price") || "",
    min_year: params.get("min_year") || "",
    max_year: params.get("max_year") || "",
    sort: params.get("sort") || "newest",
  }), [params]);

  const updateFilter = (k, v) => {
    const next = new URLSearchParams(params);
    if (!v || v === "all") next.delete(k);
    else next.set(k, v);
    setParams(next, { replace: true });
  };

  const clearAll = () => setParams(new URLSearchParams(), { replace: true });

  useEffect(() => {
    api.get("/equipment/filters").then((r) => setMeta(r.data));
  }, []);

  useEffect(() => {
    setLoading(true);
    const q = {};
    Object.entries(filters).forEach(([k, v]) => {
      if (v && v !== "all") q[k] = v;
    });
    api.get("/equipment", { params: q })
      .then((r) => setItems(r.data))
      .finally(() => setLoading(false));
  }, [filters]);

  const activeCount = Object.entries(filters).filter(
    ([k, v]) => v && v !== "all" && k !== "sort" && k !== "search"
  ).length;

  return (
    <div data-testid="inventory-page" className="mx-auto max-w-[1400px] px-6 py-12 lg:px-10">
      <div className="border-b border-white/10 pb-10">
        <div className="font-mono text-xs uppercase tracking-[0.3em] text-[#FFB800]">Inventory</div>
        <h1 className="mt-4 font-display text-5xl font-black uppercase text-white sm:text-6xl">
          {items.length} Machines <span className="text-[#FFB800]">on the lot</span>
        </h1>
      </div>

      <div className="mt-8 flex flex-col gap-4 lg:flex-row">
        {/* SIDEBAR FILTERS */}
        <aside className={`${filtersOpen ? "block" : "hidden"} lg:block lg:w-72 lg:flex-shrink-0`} data-testid="filters-sidebar">
          <div className="border border-[#27272A] bg-[#141415]">
            <div className="flex items-center justify-between border-b border-[#27272A] p-5">
              <div className="flex items-center gap-2">
                <SlidersHorizontal className="h-4 w-4 text-[#FFB800]" />
                <span className="font-display text-sm font-bold uppercase tracking-[0.2em]">Filters</span>
              </div>
              {activeCount > 0 && (
                <button
                  onClick={clearAll}
                  data-testid="clear-filters-btn"
                  className="font-mono text-[10px] uppercase tracking-wider text-[#FFB800] hover:underline"
                >
                  Clear ({activeCount})
                </button>
              )}
            </div>

            <FilterGroup label="Category">
              <SelectNative
                value={filters.category}
                onChange={(v) => updateFilter("category", v)}
                options={[{ value: "all", label: "All Categories" }, ...meta.categories.map(c => ({ value: c, label: c }))]}
                testid="filter-category"
              />
            </FilterGroup>

            <FilterGroup label="Brand">
              <SelectNative
                value={filters.brand}
                onChange={(v) => updateFilter("brand", v)}
                options={[{ value: "all", label: "All Brands" }, ...meta.brands.map(b => ({ value: b, label: b }))]}
                testid="filter-brand"
              />
            </FilterGroup>

            <FilterGroup label="Condition">
              <SelectNative
                value={filters.condition}
                onChange={(v) => updateFilter("condition", v)}
                options={[{ value: "all", label: "Any Condition" }, ...meta.conditions.map(c => ({ value: c, label: c }))]}
                testid="filter-condition"
              />
            </FilterGroup>

            <FilterGroup label="Price (USD)">
              <div className="grid grid-cols-2 gap-2">
                <input
                  data-testid="filter-min-price"
                  type="number"
                  placeholder="Min"
                  value={filters.min_price}
                  onChange={(e) => updateFilter("min_price", e.target.value)}
                  className="w-full border border-[#27272A] bg-[#0a0a0b] px-3 py-2 font-mono text-sm text-white focus:border-[#FFB800] focus:outline-none"
                />
                <input
                  data-testid="filter-max-price"
                  type="number"
                  placeholder="Max"
                  value={filters.max_price}
                  onChange={(e) => updateFilter("max_price", e.target.value)}
                  className="w-full border border-[#27272A] bg-[#0a0a0b] px-3 py-2 font-mono text-sm text-white focus:border-[#FFB800] focus:outline-none"
                />
              </div>
            </FilterGroup>

            <FilterGroup label="Year">
              <div className="grid grid-cols-2 gap-2">
                <input
                  data-testid="filter-min-year"
                  type="number"
                  placeholder="From"
                  value={filters.min_year}
                  onChange={(e) => updateFilter("min_year", e.target.value)}
                  className="w-full border border-[#27272A] bg-[#0a0a0b] px-3 py-2 font-mono text-sm text-white focus:border-[#FFB800] focus:outline-none"
                />
                <input
                  data-testid="filter-max-year"
                  type="number"
                  placeholder="To"
                  value={filters.max_year}
                  onChange={(e) => updateFilter("max_year", e.target.value)}
                  className="w-full border border-[#27272A] bg-[#0a0a0b] px-3 py-2 font-mono text-sm text-white focus:border-[#FFB800] focus:outline-none"
                />
              </div>
            </FilterGroup>
          </div>
        </aside>

        {/* RESULTS */}
        <div className="min-w-0 flex-1">
          <div className="mb-6 flex flex-col gap-3 border border-[#27272A] bg-[#141415] p-4 md:flex-row md:items-center">
            <div className="relative flex-1">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-white/40" />
              <Input
                data-testid="search-input"
                placeholder="Search by brand, model, keyword…"
                value={filters.search}
                onChange={(e) => updateFilter("search", e.target.value)}
                className="border border-[#27272A] bg-[#0a0a0b] pl-10 font-mono text-sm text-white placeholder:text-white/30 focus-visible:border-[#FFB800] focus-visible:ring-0"
                style={{ borderRadius: 0 }}
              />
            </div>
            <button
              onClick={() => setFiltersOpen(!filtersOpen)}
              className="flex items-center gap-2 border border-[#27272A] px-4 py-2 font-display text-xs font-bold uppercase tracking-widest hover:border-[#FFB800] hover:text-[#FFB800] lg:hidden"
            >
              {filtersOpen ? <X className="h-4 w-4" /> : <SlidersHorizontal className="h-4 w-4" />}
              Filters {activeCount > 0 && `(${activeCount})`}
            </button>
            <select
              data-testid="sort-select"
              value={filters.sort}
              onChange={(e) => updateFilter("sort", e.target.value)}
              className="border border-[#27272A] bg-[#0a0a0b] px-4 py-2 font-mono text-sm text-white focus:border-[#FFB800] focus:outline-none"
            >
              {SORTS.map((s) => (
                <option key={s.value} value={s.value}>{s.label}</option>
              ))}
            </select>
          </div>

          {loading ? (
            <div className="grid gap-6 sm:grid-cols-2 xl:grid-cols-3">
              {Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className="h-[420px] animate-pulse border border-[#27272A] bg-[#141415]" />
              ))}
            </div>
          ) : items.length === 0 ? (
            <div data-testid="no-results" className="border border-dashed border-[#27272A] p-16 text-center">
              <p className="font-display text-2xl font-bold uppercase text-white">No machines match</p>
              <p className="mt-2 text-sm text-white/60">Try clearing filters or broadening your search.</p>
              <button
                onClick={clearAll}
                className="mt-6 border border-[#27272A] px-6 py-3 font-display text-xs font-bold uppercase tracking-widest hover:border-[#FFB800] hover:text-[#FFB800]"
              >
                Clear all filters
              </button>
            </div>
          ) : (
            <div className="grid gap-6 sm:grid-cols-2 xl:grid-cols-3" data-testid="inventory-grid">
              {items.map((it, i) => <EquipmentCard key={it.id} item={it} index={i} />)}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

const FilterGroup = ({ label, children }) => (
  <div className="border-b border-[#27272A] p-5 last:border-b-0">
    <div className="mb-3 font-mono text-[10px] uppercase tracking-[0.25em] text-white/50">{label}</div>
    {children}
  </div>
);

const SelectNative = ({ value, onChange, options, testid }) => (
  <select
    data-testid={testid}
    value={value}
    onChange={(e) => onChange(e.target.value)}
    className="w-full border border-[#27272A] bg-[#0a0a0b] px-3 py-2 font-mono text-sm text-white focus:border-[#FFB800] focus:outline-none"
  >
    {options.map((o) => (
      <option key={o.value} value={o.value}>{o.label}</option>
    ))}
  </select>
);
